/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import {
  BPMN,
  DEFAULT_ZEEBE_VERSION,
  DEFAULT_ZEEBE_VERSION_FORMS,
  DMN,
  EXECUTION_PLATFORM,
  FORM
} from 'utils/constants';

import { createBpmnModdle, createDmnModdle } from './moddle-util';

/**
 * Upsert execution platform and version to Camunda 8 in case it's Camunda 7 or invalid.
 *
 * @param {String} type
 * @param {String} content
 *
 * @returns {Promise<String>}
 */
export default async function upsertExecutionPlatformToC8(type, content) {
  if (!type) {
    throw new Error('Type is required');
  }

  if (!content || content === '') {
    throw new Error('Content is required');
  }

  if (type === BPMN) {
    return await upsertBpmnExecutionPlatformToC8(content);
  } else if (type === DMN) {
    return await upsertDmnExecutionPlatformToC8(content);
  } else if (type === FORM) {
    return upsertFormExecutionPlatformToC8(content);
  }

  return content;
}

/**
 * Upsert execution platform and version to Camunda 8 in case it's Camunda 7 or invalid.
 *
 * @param {String} xml
 *
 * @returns {Promise<String>}
 */
export const upsertBpmnExecutionPlatformToC8 = async (xml) => {
  const moddle = createBpmnModdle();

  const { rootElement: definitions } = await moddle.fromXML(xml);

  const executionPlatform = definitions.get('executionPlatform'),
    executionPlatformVersion = definitions.get('executionPlatformVersion');

  if (!isExecutionPlatformValid(executionPlatform, executionPlatformVersion)) {
    definitions.set('executionPlatform', EXECUTION_PLATFORM);
    definitions.set('executionPlatformVersion', DEFAULT_ZEEBE_VERSION);

    ({ xml } = await moddle.toXML(definitions, { format: true }));
  }

  return xml;
};

/**
 * Upsert execution platform and version to Camunda 8 in case it's Camunda 7 or invalid.
 *
 * @param {String} xml
 *
 * @returns {Promise<String>}
 */
export const upsertDmnExecutionPlatformToC8 = async (xml) => {
  const moddle = createDmnModdle();

  const { rootElement: definitions } = await moddle.fromXML(xml);

  const executionPlatform = definitions.get('executionPlatform'),
    executionPlatformVersion = definitions.get('executionPlatformVersion');

  if (!isExecutionPlatformValid(executionPlatform, executionPlatformVersion)) {
    definitions.set('executionPlatform', EXECUTION_PLATFORM);
    definitions.set('executionPlatformVersion', DEFAULT_ZEEBE_VERSION);

    ({ xml } = await moddle.toXML(definitions, { format: true }));
  }

  return xml;
};

/**
 * Upsert execution platform and version to Camunda 8 in case it's Camunda 7 or invalid.
 *
 * @param {String} jsonString
 *
 * @returns {String}
 */
export const upsertFormExecutionPlatformToC8 = (jsonString) => {
  const json = JSON.parse(jsonString);

  const { executionPlatform, executionPlatformVersion } = json;

  if (!isExecutionPlatformValid(executionPlatform, executionPlatformVersion)) {
    json.executionPlatform = EXECUTION_PLATFORM;
    json.executionPlatformVersion = DEFAULT_ZEEBE_VERSION_FORMS;

    return JSON.stringify(json, null, 2);
  }

  return jsonString;
};

function isExecutionPlatformValid(executionPlatform, executionPlatformVersion) {
  return executionPlatform === EXECUTION_PLATFORM && executionPlatformVersion !== undefined;
}
